/**
 * TAA — Taalbegrip rulebook.
 *
 * Sourced from DEFAULT_POLICY (Taalbegrip entries — has moduleOverrides for
 * longer max question text) + MODULE_AUTHORING_GUIDE §8.
 *
 * v3 (mei 2026) — uitgebreid met hints voor ARGUMENTATIE / ONT-BIJZIN /
 * ONT-BETREKKELIJK na DOK 3-rewrite van die families; preferredTypes +
 * error_spotting/sentence_error_judge in lijn met customTypeRoles.
 */

import type { ModuleRulebook } from './types'

export const TAA_RULEBOOK: ModuleRulebook = {
  moduleId: 'Taalbegrip',
  shortCode: 'TAA',
  displayName: 'Taalbegrip',

  didactics: {
    principles: [
      'tekst_context_essentieel',
      'leesvaardigheid_lagen',
      'zinsontleding',
      'woordsoorten_definitie',
      'argumentatie_analyse',
    ],
    schoolbookReferences: ['Nieuwsbegrip', 'Taal Actief', 'Staal Taal'],
    misconceptionDomain: 'taa',
    coreSummary:
      'Tekstbegrip (feit/conclusie/hoofdgedachte), zinsontleding (zinsdelen, woordsoorten) en argumentatie.',
  },

  notation: {},

  content: {
    maxQuestionLengthByTierBand: {
      basic: 200,
      intermediate: 400,
      advanced: 600,
      expert: 800,
    },
    distractorMisconceptionPattern:
      'tekst|alinea|zin|grammatica|werkwoord|zelfstandig naamwoord|argument|stelling|bijzin|hoofdzin|betrekkelijk|antecedent|signaalwoord|hoofdgedachte|conclusie',
  },

  explanation: {
    requireRuleReference: true,
    // Vanaf tier 11 (groep 8) wordt argumentatie- en bijzin-analyse meta-talig —
    // leerlingen moeten dan kunnen controleren via een grammatica-regel.
    checkStrategyMinTier: 11,
    preferredStrategyKeywords: [
      'hoofdgedachte',
      'signaalwoord',
      'zinsdeel',
      'woordsoort',
      'argument',
      'stelling',
      'antecedent',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'readingPassage',
      'sentenceDiagram',
      'wordClassChip',
      'signalWordCard',
      'synonymAntonymPair',
      'textStructureSchema',
    ],
    familyVisualHints: [
      // — Lezen: structuur-georiënteerd —
      {
        familyIdPattern: '^TAA-LEZEN-(STRUCTUUR|SAMENVATTEN|KERN|CONCLUSIE)$',
        preferredStimulus: 'readingPassage',
        preferredSupport: 'textStructureSchema',
        note: 'Lange tekst + structuurschema als steiger.',
      },
      {
        familyIdPattern: '^TAA-LEZEN-(VERB|VERW|VOORSPEL)$',
        preferredStimulus: 'readingPassage',
        preferredSupport: 'signalWordCard',
        note: 'Lees-relaties: signaalwoorden zichtbaar maken.',
      },
      {
        familyIdPattern: '^TAA-LEZEN-',
        preferredStimulus: 'readingPassage',
        autoExemptEmbedded: true,
        note: 'Default: leestaken eisen een passage als stimulus.',
      },

      // — Argumentatie (DOK 3: analyseren van argumenten in echte teksten) —
      {
        familyIdPattern: '^TAA-ARGUMENTATIE',
        preferredStimulus: 'readingPassage',
        preferredSupport: 'textStructureSchema',
        note: 'Argumentatie-analyse vereist een echte argumentatieve tekst als stimulus; structuurschema voor stelling/argument/tegenargument als support.',
      },

      // — Zinsontleding: specifieke bijzin-families voor generieke ^TAA-ONT- —
      {
        familyIdPattern: '^TAA-ONT-(BIJZIN|BETREKKELIJK)',
        preferredStimulus: 'sentenceDiagram',
        note: 'Bijzin- en betrekkelijke-bijzin-analyse: zinsdiagram toont hoofdzin/bijzin-relatie + antecedent visueel.',
      },
      {
        familyIdPattern: '^TAA-ONT-PARSER$',
        preferredStimulus: 'sentenceDiagram',
        note: 'Parser-vraag: zinsdiagram IS de interactieve stimulus.',
      },
      {
        familyIdPattern: '^TAA-ONT-',
        preferredSupport: 'sentenceDiagram',
        autoExemptEmbedded: true,
        note: 'Zinsontleding met visueel zinsdiagram als support.',
      },

      // — Woordsoorten —
      {
        familyIdPattern: '^TAA-SOORT-WW',
        preferredSupport: 'verbTable',
        note: 'Werkwoorden: vervoegingstabel als houvast.',
      },
      {
        familyIdPattern: '^TAA-SOORT-',
        preferredSupport: 'wordClassChip',
        autoExemptEmbedded: true,
        note: 'Woordsoort-chip als visuele houvast.',
      },

      // — Woordenschat —
      {
        familyIdPattern: '^TAA-WOORD-(SYNONIEM|ANTONIEM)$',
        preferredStimulus: 'synonymAntonymPair',
        note: 'Synoniem/antoniem als gekoppeld paar tonen.',
      },
      {
        familyIdPattern: '^TAA-WOORD-(CONTEXT|IDIOOM)$',
        preferredStimulus: 'readingPassage',
        note: 'Context- en idioom-vragen: woordbetekenis in een zin/passage afleiden, niet uit een woordenboek.',
      },

      // — Tekstsoorten en signaalwoorden —
      {
        familyIdPattern: '^TAA-TEKSTSOORT',
        preferredSupport: 'textStructureSchema',
        note: 'Tekstsoort herkennen via structuurschema.',
      },
      {
        familyIdPattern: '^TAA-SIGNAALWOORD',
        preferredSupport: 'signalWordCard',
        note: 'Signaalwoorden als kaartje tonen.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'fill_in',
      'text_highlight',
      'sentence_parser',
      'matching',
      'categorize',
      'error_spotting',
      'sentence_error_judge',
    ],
    /**
     * EXTENSION-POINT EXAMPLE — how to register custom / specialised
     * question types for this module. As we scale to 100+ types, each
     * new type adds one entry here; nothing else has to change in the
     * pipeline. `didacticRole` + `preferredDok` give the pedagogy stage
     * enough signal to reason about DOK alignment.
     */
    customTypeRoles: [
      {
        type: 'text_highlight',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Markeren in tekst dwingt locatie-bewust lezen — beter dan losse MC.',
      },
      {
        type: 'sentence_parser',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Zinsdelen identificeren is een meerstaps-redenering, hoort op DOK 3.',
      },
      {
        type: 'error_spotting',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 6, max: 16 },
        reason:
          'Foutdetectie vereist meta-cognitie over de regel, niet alleen toepassing.',
      },
      {
        type: 'sentence_error_judge',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 9, max: 16 },
        reason:
          'Grammaticale juistheid beoordelen vereist regel-toepassing op nieuwe zin — kern van bijzin/betrekkelijk-werk op DOK 3.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^TAA-LEZEN-FEIT',
        preferredTypes: [
          'multiple_choice',
          'true_false_series',
          'text_highlight',
        ],
        note: 'Feit-vragen: true_false_series is hier WEL didactisch sterk — feiten herkennen vs. interpretaties is kernvaardigheid.',
      },
      {
        familyIdPattern:
          '^TAA-LEZEN-(CONCLUSIE|KERN|DOEL|VOORSPEL|STRUCTUUR|VERB|VERW|SAMENVATTEN)',
        preferredTypes: [
          'multiple_choice',
          'text_highlight',
          'matching',
          'categorize',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Hogere leesvaardigheid (conclusie/kern/doel): interpretatie en redeneren, geen herkenning.',
      },
      {
        familyIdPattern: '^TAA-ONT-',
        preferredTypes: [
          'sentence_parser',
          'sentence_error_judge',
          'fill_in',
          'matching',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Zinsontleding: structuur identificeren of regel toepassen — vereist analyse, geen evaluatie.',
      },
      {
        familyIdPattern: '^TAA-SOORT-',
        preferredTypes: [
          'categorize',
          'matching',
          'text_highlight',
          'multiple_choice',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Woordsoorten: classificatie in context — categorize/matching forceren volledige indeling.',
      },
      {
        familyIdPattern: '^TAA-WOORD-',
        preferredTypes: ['multiple_choice', 'matching', 'fill_in'],
        note: 'Woordbetekenis/synoniem/antoniem: contextueel lezen of koppelen.',
      },
      {
        familyIdPattern: '^TAA-ARGUMENTATIE',
        preferredTypes: [
          'multiple_choice',
          'text_highlight',
          'categorize',
          'true_false_series',
        ],
        note: 'Argumentatie-analyse: claim/onderbouwing herkennen — bewerings-evaluatie hier passend op DOK 2.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'literal-vs-inferred',
      label: 'Letterlijk vs. afgeleid',
      description:
        'Letterlijke tekst geven bij vraag om conclusie (of andersom).',
    },
    {
      id: 'similar-word-class',
      label: 'Verwante woordsoort',
      description:
        'Bijwoord aanduiden als bijvoeglijk naamwoord — vergelijkbare maar foute categorie.',
    },
    {
      id: 'antecedent-mismatch',
      label: 'Antecedent-fout',
      description:
        'Betrekkelijk voornaamwoord (die/dat) verwijzen naar verkeerd antecedent — kerntrap bij betrekkelijke bijzinnen.',
    },
    {
      id: 'mainclause-vs-subclause',
      label: 'Hoofdzin vs. bijzin',
      description:
        'Onderdeel van de hoofdzin aanduiden als bijzin (of andersom) — werkwoordpositie negeren.',
    },
  ],

  keyRules: [
    'Tekst-context altijd meegeven bij leesvragen',
    'Woordsoort-definitie vermelden in uitleg',
    'Signaalwoorden expliciet benoemen',
    'Trap-distractor: vergelijkbare maar foute woordsoort',
    'Maximale vraaglengte is hoger dan default (lees-passages mogen lang zijn)',
    'Argumentatie/bijzin/betrekkelijk: vereisen DOK 3 — analyseren in echte zinnen, niet definitie-recall',
  ],

  authoringGuideAnchor: '#8-taa--taalbeschouwing',
}
